import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Button, Code, CodeBlock, ConfirmDialog, CopyButton, Notice, StatusDot } from '@/ui'

/**
 * Settings › Stats API. The server issues the key and the panel shows it in
 * full, so it can be copied into whatever pulls the stats (report sources).
 */
export default function StatsApiPanel() {
  const qc = useQueryClient()
  const info = useQuery({ queryKey: ['stats-token'], queryFn: api.statsToken })
  const [shown, setShown] = useState<string | null>(null)
  const [justIssued, setJustIssued] = useState(false)
  const [confirm, setConfirm] = useState<'issue' | 'delete' | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const reveal = useMutation({
    mutationFn: api.statsTokenValue,
    onSuccess: (r) => {
      setShown(r.token)
      setJustIssued(false)
    },
    onError: (e) => setErr((e as Error).message),
  })
  const issue = useMutation({
    mutationFn: api.issueStatsToken,
    onSuccess: (r) => {
      setShown(r.token)
      setJustIssued(true)
      setConfirm(null)
      qc.invalidateQueries({ queryKey: ['stats-token'] })
    },
    onError: (e) => setErr((e as Error).message),
  })
  const remove = useMutation({
    mutationFn: api.deleteStatsToken,
    onSuccess: () => {
      setShown(null)
      setConfirm(null)
      qc.invalidateQueries({ queryKey: ['stats-token'] })
    },
    onError: (e) => setErr((e as Error).message),
  })

  const d = info.data
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const example = `curl -H "Authorization: Bearer ${shown ?? '<키>'}" \\\n  "${origin}${d?.path ?? '/api/v1/stats'}?by=month&from=2026-06"`

  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <header className="border-b border-border px-6 py-4">
        <h2 className="text-base font-semibold text-card-foreground">Stats API</h2>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          외부(리포트 등)에서 수익 통계를 읽는 키. 요청 헤더에 <Code>Authorization: Bearer &lt;키&gt;</Code>를 붙인다.
        </p>
      </header>

      {err && (
        <div className="border-b border-border px-6 py-3">
          <Notice tone="failed" role="alert" title="처리하지 못했다" detail={err} />
        </div>
      )}

      <div className="space-y-4 px-6 py-4">
        {info.isLoading ? (
          <p className="text-sm text-muted-foreground">불러오는 중…</p>
        ) : info.isError ? (
          <p className="text-sm text-status-failed">{(info.error as Error).message}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <StatusDot tone={d?.enabled ? 'published' : 'neutral'} />
              {d?.enabled ? '켜짐' : '꺼짐 — 키를 발급하면 켜진다'}
            </span>
            {d?.enabled && (
              <>
                <Code>{d.preview}</Code>
                <span className="text-xs text-muted-foreground">
                  {d.source === 'env' ? '환경변수(STATS_API_TOKEN) 키' : '설정에 저장된 키'}
                </span>
              </>
            )}
          </div>
        )}

        {shown && (
          <div className="space-y-2">
            {justIssued && (
              <Notice tone="publishing" title="새 키다. 이전 키는 지금부터 막힌다.">
                이 키를 쓰는 곳(리포트 소스 <Code>~/.config/pentarab/adfusion-stats-token</Code> 등)도 바꾼다.
              </Notice>
            )}
            <div className="flex items-stretch gap-2">
              <input
                readOnly
                value={shown}
                onFocus={(e) => e.currentTarget.select()}
                className="h-9 min-w-0 flex-1 rounded-md border border-input bg-muted/50 px-3 font-mono text-sm text-foreground"
                aria-label="API 키"
              />
              <CopyButton text={shown} label="복사" what="API 키" size="md" />
              <Button onClick={() => setShown(null)}>숨기기</Button>
            </div>
          </div>
        )}

        <div>
          <div className="mb-1 text-xs text-muted-foreground">
            사용 예 · <Code>by=total|day|month|app|country|app_month|day_app_country</Code>, <Code>from</Code>/<Code>to</Code> = YYYY-MM-DD 또는 YYYY-MM,{' '}
            <Code>limit</Code>, <Code>include_zero=1</Code>
          </div>
          <CodeBlock className="overflow-x-auto whitespace-pre">{example}</CodeBlock>
        </div>
      </div>

      <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-muted/50 px-4 py-3 sm:px-6">
        {d?.source === 'settings' && (
          <Button variant="dangerOutline" onClick={() => setConfirm('delete')} disabled={remove.isPending}>
            끄기
          </Button>
        )}
        {d?.enabled && !shown && (
          <Button onClick={() => reveal.mutate()} disabled={reveal.isPending}>
            {reveal.isPending ? '불러오는 중…' : '키 보기'}
          </Button>
        )}
        <Button
          variant="primary"
          onClick={() => (d?.enabled ? setConfirm('issue') : issue.mutate())}
          disabled={issue.isPending || info.isLoading}
        >
          {issue.isPending ? '발급 중…' : d?.enabled ? '새 키 발급' : '키 발급'}
        </Button>
      </footer>

      <ConfirmDialog
        open={confirm === 'issue'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="새 키를 발급할까?"
        description="지금 키는 바로 막힌다. 이 키를 쓰는 곳은 새 키로 바꿔야 한다."
        confirmLabel="발급"
        cancelLabel="취소"
        loading={issue.isPending}
        onConfirm={() => issue.mutate()}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Stats API를 끌까?"
        description="저장된 키를 지운다. 이 키로 읽던 곳은 바로 막힌다."
        confirmLabel="끄기"
        cancelLabel="취소"
        confirmVariant="destructive"
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </section>
  )
}
